'use client'

import { DataRenderer, TypeBadge } from '@/components/ui/ExecutionDataDisplay'
import { NodeDataSection } from '@/components/ui/NodeDataDisplay'
import { OutputSchemaDisplay } from '@/components/ui/OutputSchemaDisplay'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { useGetUserAdminStatusQuery } from '@/lib/features/user/userApi'
import { jsonSchemaStringToZod } from '@/lib/utils/jsonSchemaToZod'
import { convertWorkflowNodeToFrontendData } from '@/lib/utils/zod-workflow-converter'
import { getDisplayName, getToolColor, getToolIcon } from '@/utils/workflowToolMappings'
import { ChevronRight, Copy, Eye } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import { getToolResultSchema, TOOL_DEFINITIONS } from 'sprites-types/workflow/tools'
import { z } from 'zod'
import { FlowchartNode as FlowchartNodeType } from './layoutUtils'

interface NodeExecutionDetailsContentProps {
  executionId?: string
  nodeExecution?: NodeExecution
  nodeData: FlowchartNodeType
  workflowConfig?: WorkflowConfig
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

const VISUAL_START_NODE = {
  id: '__start__',
  name: 'Start',
  description: 'Workflow entry point',
  toolId: 'start',
  edges: [],
  inputs: {},
}

const VISUAL_END_NODE = {
  id: '__end__',
  name: 'End',
  description: 'Workflow exit point',
  toolId: 'end',
  edges: [],
  inputs: {},
}

function canViewNodeExecution(nodeExecution: NodeExecution) {
  const visibleStatuses = ['completed', 'failed', 'errored']
  return visibleStatuses.includes(nodeExecution.status)
}

export function NodeExecutionDetailsContent({
  executionId,
  nodeExecution: passedNodeExecution,
  nodeData,
  workflowConfig,
  onOpenPanel,
}: NodeExecutionDetailsContentProps) {
  // Get latest node execution data from context
  const { workflowState } = useCopilotContext()

  // Prioritize passed nodeExecution over state lookup for better reliability
  const nodeExecution =
    passedNodeExecution || (executionId ? workflowState.nodeExecutions?.[executionId] : undefined)

  // State for collapsible sections - default expanded based on nodeExecution presence
  const [isRawDataExpanded, setIsRawDataExpanded] = useState(false)
  const [isInputsExpanded, setIsInputsExpanded] = useState(!!nodeExecution)
  const [isOutputsExpanded, setIsOutputsExpanded] = useState(!!nodeExecution)
  const [isNodeDetailsExpanded, setIsNodeDetailsExpanded] = useState(
    !nodeExecution || !canViewNodeExecution(nodeExecution)
  )
  const { data: adminStatus } = useGetUserAdminStatusQuery()
  const searchParams = useSearchParams()
  const isDebugMode = searchParams.get('debug') === 'true'

  const formatDuration = (startTime: string, endTime: string) => {
    const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime()

    if (durationMs < 1000) {
      return `${durationMs}ms`
    } else if (durationMs < 60000) {
      return `${Math.round(durationMs / 1000)}s`
    } else {
      const minutes = Math.floor(durationMs / 60000)
      const seconds = Math.round((durationMs % 60000) / 1000)
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200/60 shadow-sm'
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200/60 shadow-sm'
      case 'running':
      case 'pending':
        return 'bg-blue-50 text-blue-700 border-blue-200/60 shadow-sm'
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200/60 shadow-sm'
    }
  }

  const handleOpenToolResultDisplay = () => {
    if (!onOpenPanel || !nodeExecution) return

    // Create a minimal workflow config for the WorkflowToolResult component
    // Note: We need to extract the inputs from nodeExecution since it contains the full execution data
    const minimalWorkflowConfig: WorkflowConfig = {
      id: 'debug-workflow',
      name: 'Debug View',
      description: 'Temporary workflow for debugging',
      start: nodeData.id,
      parameters: {
        type: 'object',
        properties: {},
      },
      nodes: [
        {
          id: nodeData.id,
          toolId: nodeData.toolId,
          name: nodeData.name,
          description: nodeData.description || '',
          // Store the raw inputs here - they will be accessed via nodeExecution.inputs by the display component
          inputs: nodeExecution.inputs || {},
          edges: [],
        },
      ],
    }

    onOpenPanel(
      {
        type: 'workflow-tool-result',
        nodeExecution,
        workflowConfig: minimalWorkflowConfig,
      },
      [{ name: 'Tool Result Display', index: 0 }]
    )
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50/30">
      <div className="p-6">
        {/* Node Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 text-white shadow-lg backdrop-blur-sm"
              style={{
                background: `linear-gradient(135deg, ${getToolColor(nodeData.toolId).color}dd 0%, ${getToolColor(nodeData.toolId).color} 100%)`,
                boxShadow: `0 4px 12px ${getToolColor(nodeData.toolId).color}30, 0 0 0 1px ${getToolColor(nodeData.toolId).color}20`,
              }}
            >
              {(() => {
                const IconComponent = getToolIcon(nodeData.toolId)
                return <IconComponent size={24} />
              })()}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {nodeData.name || 'Node Execution Results'}
              </h1>
              <p className="text-sm font-medium text-gray-600">
                {getDisplayName(nodeData.toolId || '')} • {nodeData.id}
              </p>
            </div>
          </div>
        </div>

        {/* Execution Details */}
        {nodeExecution && canViewNodeExecution(nodeExecution) && (
          <div className="mb-8">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">Execution Details</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Status
                </div>
                <div
                  className={`mt-2 inline-block rounded-xl border px-4 py-2 text-sm font-medium ${getStatusColor(nodeExecution.status || 'unknown')}`}
                >
                  {nodeExecution.status || 'Unknown'}
                </div>
              </div>

              {nodeExecution.startedAt && (
                <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Started
                  </div>
                  <div className="mt-2 text-lg font-semibold text-gray-900">
                    {new Date(nodeExecution.startedAt).toLocaleString()}
                  </div>
                </div>
              )}

              {nodeExecution.completedAt && (
                <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Completed
                  </div>
                  <div className="mt-2 text-lg font-semibold text-gray-900">
                    {new Date(nodeExecution.completedAt).toLocaleString()}
                  </div>
                </div>
              )}

              {nodeExecution.inputs?.functionArgs?.emitMode && (
                <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Emit Mode
                  </div>
                  <div className="mt-2 text-xl font-semibold text-gray-900">
                    {nodeExecution.inputs.functionArgs.emitMode}
                  </div>
                </div>
              )}

              {nodeExecution.startedAt && nodeExecution.completedAt && (
                <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Duration
                  </div>
                  <div className="mt-2 text-xl font-semibold text-gray-900">
                    {formatDuration(nodeExecution.startedAt, nodeExecution.completedAt)}
                  </div>
                </div>
              )}

              {nodeExecution.credits != null && (
                <div className="rounded-2xl border border-gray-200/60 bg-white/80 p-5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/90 hover:shadow-md">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Credits Used
                  </div>
                  <div className="mt-2 text-xl font-semibold text-gray-900">
                    {nodeExecution.credits}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Execution Inputs */}
        {nodeExecution?.inputs?.functionArgs && canViewNodeExecution(nodeExecution) && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsInputsExpanded(!isInputsExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Execution Inputs</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      Function arguments passed to the tool
                    </p>
                  </div>
                  <ChevronRight
                    size={20}
                    className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isInputsExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                  />
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isInputsExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <div className="space-y-4">
                    {(() => {
                      const { ...inputs } = nodeExecution.inputs.functionArgs
                      const inputEntries = Object.entries(inputs)

                      return inputEntries.length > 0 ? (
                        inputEntries
                          .filter(([key]) => key !== 'emitMode')
                          .map(([key, value]) => (
                            <div
                              key={key}
                              className="rounded-xl border border-gray-200 p-4 shadow-sm"
                            >
                              <div className="mb-4 flex items-center gap-2">
                                <h4 className="text-base font-medium text-gray-800">{key}</h4>
                                <TypeBadge value={value} />
                              </div>
                              <DataRenderer data={value} />
                            </div>
                          ))
                      ) : (
                        <div className="rounded-xl border border-gray-200 p-4 shadow-sm">
                          <p className="text-sm text-gray-600">No inputs to display</p>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Execution Outputs */}
        {nodeExecution?.outputs && canViewNodeExecution(nodeExecution) && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <div className="flex items-center border-b border-gray-200/50 bg-white/50 backdrop-blur-sm">
                <button
                  onClick={() => setIsOutputsExpanded(!isOutputsExpanded)}
                  className="flex-1 px-6 py-4 text-left transition-all duration-200 hover:bg-white/70 focus:outline-none"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h3 className="text-base font-semibold text-gray-900">Execution Outputs</h3>
                      <p className="mt-1 text-sm font-medium text-gray-600">
                        Results returned by the tool execution
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDebugMode && onOpenPanel && (
                        <button
                          onClick={handleOpenToolResultDisplay}
                          className="flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 transition-all hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                          title="Open tool result display component"
                        >
                          <Eye size={14} />
                          View Display
                        </button>
                      )}
                      <ChevronRight
                        size={20}
                        className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                          isOutputsExpanded ? 'rotate-90' : 'rotate-0'
                        }`}
                      />
                    </div>
                  </div>
                </button>
              </div>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isOutputsExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <div className="space-y-4">
                    {Array.isArray(nodeExecution.outputs) ? (
                      // Handle array of outputs
                      nodeExecution.outputs.map((output, index) => (
                        <div
                          key={index}
                          className="rounded-xl border border-gray-200 p-4 shadow-sm"
                        >
                          <div className="mb-4 flex items-center gap-2">
                            <h4 className="text-base font-medium text-gray-800">
                              Output {index + 1}
                            </h4>
                            <TypeBadge value={output} />
                          </div>
                          <DataRenderer data={output} />
                        </div>
                      ))
                    ) : typeof nodeExecution.outputs === 'object' &&
                      nodeExecution.outputs !== null ? (
                      // Handle object with keys
                      Object.entries(nodeExecution.outputs).map(([key, value]) => (
                        <div key={key} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                          <div className="mb-4 flex items-center gap-2">
                            <h4 className="text-base font-medium text-gray-800">{key}</h4>
                            <TypeBadge value={value} />
                          </div>
                          <DataRenderer data={value} />
                        </div>
                      ))
                    ) : (
                      // Handle primitive values (string, number, etc.)
                      <div className="rounded-xl border border-gray-200 p-4 shadow-sm">
                        <div className="mb-4 flex items-center gap-2">
                          <h4 className="text-base font-medium text-gray-800">Output</h4>
                          <TypeBadge value={nodeExecution.outputs} />
                        </div>
                        <DataRenderer data={nodeExecution.outputs} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Node Configuration Details */}
        {workflowConfig && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsNodeDetailsExpanded(!isNodeDetailsExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Node Configuration</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      Description, schemas, and connections
                    </p>
                  </div>
                  <ChevronRight
                    size={20}
                    className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isNodeDetailsExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                  />
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isNodeDetailsExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-6 p-6">
                  {/* Node Description */}
                  {nodeData.description && (
                    <div>
                      <h2 className="mb-3 text-base font-semibold text-gray-900">Description</h2>
                      <p className="text-sm leading-relaxed text-gray-600">
                        {nodeData.description}
                      </p>
                    </div>
                  )}

                  {/* Input Schema */}
                  {(() => {
                    if (!workflowConfig || !nodeData.toolId) return null

                    const node = workflowConfig.nodes.find((n) => n.id === nodeData.id)
                    if (!node) return null

                    const toolMeta =
                      TOOL_DEFINITIONS[nodeData.toolId as keyof typeof TOOL_DEFINITIONS]
                    const inputSchema = toolMeta?.argsSchema

                    if (!inputSchema) return null

                    try {
                      const frontendData = convertWorkflowNodeToFrontendData(
                        node,
                        inputSchema,
                        false
                      )

                      if (!frontendData.inputs || frontendData.inputs.length === 0) return null

                      return (
                        <div>
                          <h2 className="mb-4 text-base font-semibold text-gray-900">
                            Input Schema
                          </h2>
                          <NodeDataSection title="" fields={frontendData.inputs} editMode={false} />
                        </div>
                      )
                    } catch (error) {
                      console.error('Failed to display input schema:', error)
                      return null
                    }
                  })()}

                  {/* Output Schema */}
                  {(() => {
                    if (!workflowConfig || !nodeData.toolId) return null

                    const node = workflowConfig.nodes.find((n) => n.id === nodeData.id)
                    if (!node) return null

                    const toolMeta =
                      TOOL_DEFINITIONS[nodeData.toolId as keyof typeof TOOL_DEFINITIONS]
                    const nodeInputs = node.inputs || {}

                    let outputSchema: z.ZodType<any, any, any> | undefined = undefined
                    let outputSchemaSource: 'responseSchema' | 'outputSchema' | null = null

                    // Helper to get schema string from input
                    const getSchemaString = (input: unknown): string | null => {
                      if (typeof input === 'string' && input.trim().length > 0) {
                        return input.trim()
                      }
                      if (input && typeof input === 'object') {
                        try {
                          return JSON.stringify(input)
                        } catch {
                          return null
                        }
                      }
                      return null
                    }

                    const responseSchemaRaw = (nodeInputs as Record<string, unknown>)[
                      'responseSchema'
                    ]
                    const codeOutputSchemaRaw = (nodeInputs as Record<string, unknown>)[
                      'outputSchema'
                    ]
                    const emitMode = (nodeInputs as Record<string, unknown>)['emitMode']

                    const responseSchemaString = getSchemaString(responseSchemaRaw)
                    const codeOutputSchemaString = getSchemaString(codeOutputSchemaRaw)

                    const hasResponseSchema = responseSchemaString !== null
                    const hasOutputSchema = codeOutputSchemaString !== null

                    // Prioritize responseSchema/outputSchema when present
                    if (hasResponseSchema || hasOutputSchema) {
                      const schemaString = hasResponseSchema
                        ? responseSchemaString!
                        : codeOutputSchemaString!
                      const zodFromJson = jsonSchemaStringToZod(schemaString)

                      if (zodFromJson) {
                        outputSchema = emitMode === 'multi' ? z.array(zodFromJson) : zodFromJson
                        outputSchemaSource = hasResponseSchema ? 'responseSchema' : 'outputSchema'
                      }
                    } else if (toolMeta) {
                      // Fall back to tool metadata
                      try {
                        if (Object.keys(nodeInputs).length > 0) {
                          outputSchema = getToolResultSchema(toolMeta, nodeInputs)
                        } else {
                          outputSchema = toolMeta.resultSchema.defaultSchema
                        }
                      } catch (error) {
                        console.warn(
                          `Failed to resolve result schema for tool ${toolMeta.id}:`,
                          error
                        )
                        outputSchema = toolMeta.resultSchema?.defaultSchema
                      }
                    }

                    if (!outputSchema) return null

                    return (
                      <div>
                        <h2 className="mb-4 text-base font-semibold text-gray-900">
                          Output Schema
                        </h2>
                        <OutputSchemaDisplay title="" schema={outputSchema} />
                        {outputSchemaSource && (
                          <div className="mt-3 text-xs text-gray-500">
                            <span className="rounded bg-blue-50 px-2 py-0.5 text-blue-700">
                              Derived from {outputSchemaSource}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Node Connections */}
                  {(() => {
                    if (!workflowConfig) return null

                    const node = workflowConfig.nodes.find((n) => n.id === nodeData.id)
                    if (!node) return null

                    // Helper to get node type color (matching NodeDetails)
                    const getNodeTypeColor = (toolId: string) => {
                      const colors: Record<string, string> = {
                        start: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                        end: 'bg-red-50 border-red-200 text-red-800',
                      }
                      return colors[toolId] || 'bg-gray-50 border-gray-200 text-gray-800'
                    }

                    // Get connected nodes using the same logic as NodeDetails
                    const getConnectedNodes = (nodeId: string) => {
                      const nodes = workflowConfig.nodes

                      // Find incoming nodes - any node that has edges pointing to this node
                      let incoming = nodes.filter((n) => n.edges?.some((e) => e.target === nodeId))

                      // Find outgoing nodes - this node's edges point to other nodes
                      const foundNode = nodes.find((n) => n.id === nodeId)
                      let outgoing: any[] = []

                      if (foundNode) {
                        outgoing =
                          foundNode.edges
                            ?.map((e) => {
                              if (e.target === '__end__') {
                                return VISUAL_END_NODE
                              }
                              return nodes.find((n) => n.id === e.target)
                            })
                            .filter((n) => n !== undefined) || []
                      }

                      // Check if this node is a workflow start node - if so, add visual start node to incoming
                      const startNodes = Array.isArray(workflowConfig.start)
                        ? workflowConfig.start
                        : [workflowConfig.start]
                      if (startNodes.includes(nodeId)) {
                        incoming = [VISUAL_START_NODE, ...incoming]
                      }

                      // Check if this node has success terminal edges - if so, add visual end node to outgoing
                      if (foundNode) {
                        const hasSuccessTerminalEdges = foundNode.edges?.some(
                          (e) => 'terminal' in e && e.terminal === 'success'
                        )
                        if (hasSuccessTerminalEdges) {
                          outgoing = [...outgoing, VISUAL_END_NODE]
                        }
                      }

                      return { incoming, outgoing }
                    }

                    const { incoming } = getConnectedNodes(nodeData.id)

                    // Only render the section if there are actual connections
                    if (incoming.length === 0 && (!node.edges || node.edges.length === 0))
                      return null

                    return (
                      <div>
                        <h2 className="mb-4 text-base font-semibold text-gray-900">Connections</h2>
                        <div className="space-y-3">
                          {/* Regular incoming connections */}
                          {incoming.length > 0 && (
                            <div>
                              <h5 className="mb-2 text-sm font-medium text-gray-800">
                                Incoming Connections
                              </h5>
                              <div className="space-y-2">
                                {incoming.map((incomingNode: any) => (
                                  <div
                                    key={`incoming-${incomingNode.id}`}
                                    className={`rounded-lg border p-2 ${getNodeTypeColor(incomingNode.toolId)}`}
                                  >
                                    <div className="text-sm font-medium">{incomingNode.name}</div>
                                    <div className="text-xs opacity-75">{incomingNode.toolId}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Regular outgoing connections (edges) */}
                          {node.edges && node.edges.length > 0 && (
                            <div>
                              <h5 className="mb-2 text-sm font-medium text-gray-800">
                                Outgoing Connections
                              </h5>
                              <div className="space-y-2">
                                {node.edges.map((edge: any, edgeIndex: number) => {
                                  const targetNode = workflowConfig.nodes.find(
                                    (n) => n.id === edge.target
                                  )

                                  // Check if this is a terminal edge
                                  const isTerminal = 'terminal' in edge
                                  const isSuccessTerminal =
                                    'terminal' in edge && edge.terminal === 'success'
                                  const isFailureTerminal =
                                    'terminal' in edge && edge.terminal === 'failure'

                                  return (
                                    <div
                                      key={`${nodeData.id}-edge-${edgeIndex}`}
                                      className={`rounded-lg border p-3 ${
                                        isTerminal
                                          ? isSuccessTerminal
                                            ? 'border-green-200 bg-green-50'
                                            : isFailureTerminal
                                              ? 'border-red-200 bg-red-50'
                                              : 'border-gray-200 bg-gray-50'
                                          : 'border-gray-200 bg-gray-50'
                                      }`}
                                    >
                                      <div className="mb-2 flex items-center gap-2">
                                        <div className="flex items-center gap-1">
                                          <span
                                            className={`text-sm font-medium ${
                                              isFailureTerminal ? 'text-red-700' : 'text-gray-700'
                                            }`}
                                          >
                                            {edge.tag}
                                          </span>
                                          {isFailureTerminal && (
                                            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                                              FAIL
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-500">→</span>
                                        <div className="text-xs text-gray-500">
                                          {edge.description}
                                        </div>
                                        {isTerminal && (
                                          <span
                                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                              isSuccessTerminal
                                                ? 'bg-green-100 text-green-700'
                                                : isFailureTerminal
                                                  ? 'bg-red-100 text-red-700'
                                                  : 'bg-gray-100 text-gray-700'
                                            }`}
                                          >
                                            {isSuccessTerminal
                                              ? 'Success'
                                              : isFailureTerminal
                                                ? 'Failure'
                                                : 'Terminal'}
                                          </span>
                                        )}
                                      </div>

                                      {/* Show edge condition if it exists */}
                                      {edge.condition && (
                                        <div className="mb-2 rounded bg-blue-50 p-2 text-xs text-blue-800">
                                          <span className="font-medium">Condition:</span>{' '}
                                          <code className="rounded bg-blue-100 px-1 py-0.5 font-mono">
                                            {edge.condition}
                                          </code>
                                        </div>
                                      )}

                                      {/* Show failure reason for terminal failure edges */}
                                      {isFailureTerminal &&
                                        'failureReason' in edge &&
                                        edge.failureReason && (
                                          <div className="mb-2 rounded bg-red-100 p-2 text-xs text-red-800">
                                            <span className="font-medium">Failure Reason:</span>{' '}
                                            {edge.failureReason}
                                          </div>
                                        )}

                                      {/* Show target node for non-terminal edges */}
                                      {targetNode && (
                                        <div
                                          className={`rounded-lg border p-2 ${getNodeTypeColor(targetNode.toolId)}`}
                                        >
                                          <div className="text-sm font-medium">
                                            {targetNode.name}
                                          </div>
                                          <div className="text-xs opacity-75">
                                            {targetNode.toolId}
                                          </div>
                                        </div>
                                      )}

                                      {/* Show terminal indicator for terminal edges without target */}
                                      {isTerminal && !targetNode && (
                                        <div
                                          className={`rounded-lg border p-2 ${
                                            isSuccessTerminal
                                              ? 'border-green-200 bg-green-50'
                                              : isFailureTerminal
                                                ? 'border-red-200 bg-red-50'
                                                : 'border-gray-200 bg-gray-50'
                                          }`}
                                        >
                                          <div className="text-sm font-medium">
                                            {isSuccessTerminal
                                              ? 'Workflow Success'
                                              : isFailureTerminal
                                                ? 'Workflow Failure'
                                                : 'Workflow End'}
                                          </div>
                                          <div className="text-xs opacity-75">
                                            {isSuccessTerminal
                                              ? 'Execution completed successfully'
                                              : isFailureTerminal
                                                ? 'Execution failed'
                                                : 'Execution terminated'}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Raw Execution Data */}
        {nodeExecution && adminStatus && adminStatus.isAdmin && (
          <div className="mb-8">
            <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white/80 shadow-sm backdrop-blur-sm">
              <button
                onClick={() => setIsRawDataExpanded(!isRawDataExpanded)}
                className="w-full border-b border-gray-200/50 bg-white/50 px-6 py-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/70 focus:outline-none"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-base font-semibold text-gray-900">Raw Execution Data</h3>
                    <p className="mt-1 text-sm font-medium text-gray-600">
                      Full JSON data for debugging and analysis
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Execution ID:</span>
                      <span className="font-mono text-xs text-gray-700">
                        {nodeExecution.executionId || 'N/A'}
                      </span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(nodeExecution.executionId || '')
                          toast.success('Copied Execution ID to clipboard')
                        }}
                        className="cursor-pointer rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        title="Copy execution ID"
                      >
                        <Copy size={12} />
                      </span>
                    </div>
                  </div>
                  <ChevronRight
                    size={20}
                    className={`flex-shrink-0 text-gray-500 transition-transform duration-300 ease-in-out ${
                      isRawDataExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                  />
                </div>
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isRawDataExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6">
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl bg-gray-900 p-5 font-mono text-sm text-gray-100 shadow-inner">
                    {JSON.stringify(nodeExecution, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
